// Card pieces of the sandbox playground: a player's cards, paying by card at
// a shop, and the shop's side (capture, release, refund).
import { useMemo, useState, type CSSProperties } from "react";
import { ActionMenu, Button, Confirm, CopyButton, ErrorNotice, Field, Input, Modal, Money, Panel, Select, useToast } from "../../components/ui";
import { ApiError, type GameApi } from "../../lib/api";
import { newIdempotencyKey } from "../../lib/ids";
import { formatMoney, parseMoney } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { formatGameTime } from "../../lib/time";
import type { Account, Bank, Card, CardAuthorization, IssueCardResult } from "../../lib/types";

const CARD_STATUS: Record<Card["status"], { label: string; tone: string }> = {
  active: { label: "Active", tone: "ok" },
  blocked: { label: "Blocked", tone: "" },
  lost: { label: "Lost", tone: "bad" },
  stolen: { label: "Stolen", tone: "bad" },
  expired: { label: "Expired", tone: "bad" },
  closed: { label: "Closed", tone: "bad" },
};

const AUTH_STATUS: Record<CardAuthorization["status"], { label: string; tone: string }> = {
  approved: { label: "Approved", tone: "" },
  declined: { label: "Declined", tone: "bad" },
  captured: { label: "Captured", tone: "ok" },
  released: { label: "Released", tone: "" },
  expired: { label: "Expired", tone: "bad" },
  refunded: { label: "Refunded", tone: "" },
};

/** "4242 · 03/31" */
export const cardLabel = (c: Pick<Card, "last4" | "expiry_month" | "expiry_year">) =>
  `•••• ${c.last4} · ${String(c.expiry_month).padStart(2, "0")}/${String(c.expiry_year).slice(-2)}`;

/** "5058 12•• •••• 4242" from a full or masked number. */
export const groupNumber = (n: string) => n.replace(/\*/g, "•").replace(/(.{4})(?=.)/g, "$1 ");

/** The bank's brand colour as #rrggbb, or null if it isn't a hex colour. */
export function brandHex(v: unknown): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(v ?? "").trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].replace(/./g, "$&$&") : m[1];
  return `#${h.toLowerCase()}`;
}

/** Dark ink on light brand colours, white on dark ones (WCAG relative luminance). */
export function cardInk(hex: string): "light" | "dark" {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? "dark" : "light";
}

const expiry = (c: Pick<Card, "expiry_month" | "expiry_year">) => `${String(c.expiry_month).padStart(2, "0")}/${String(c.expiry_year).slice(-2)}`;

/**
 * A debit card drawn at ID-1 proportions in the bank's colour. The back holds
 * the CVV on the signature strip, as on a real card; "Show CVV" flips it.
 */
export function CardFace({ number, cvv, card, bankName, color }: {
  number: string; cvv?: string; card: Pick<Card, "name_on_card" | "expiry_month" | "expiry_year">; bankName: string; color?: unknown;
}) {
  const [flipped, setFlipped] = useState(false);
  const hex = brandHex(color) ?? "#0b7a69";
  const style = { "--card": hex } as CSSProperties;
  return (
    <div className="card-wrap">
      <div className={`card3d${flipped ? " flipped" : ""}`} data-ink={cardInk(hex)} style={style}>
        <div className="card-face card-front" aria-hidden={flipped} inert={flipped}>
          <div className="card-top">
            <span className="card-bank">{bankName}</span>
            <span className="card-kind">Debit</span>
          </div>
          <div className="card-mid">
            <span className="card-chip" aria-hidden="true"><i /><i /><i /></span>
            <svg className="card-nfc" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-label="Contactless">
              <path d="M8.5 7.5a6.5 6.5 0 0 1 0 9M12 5a10 10 0 0 1 0 14M15.5 2.5a13.5 13.5 0 0 1 0 19" />
            </svg>
          </div>
          <div className="card-number" aria-label={`Card number ${number}`}>{groupNumber(number)}</div>
          <div className="card-bottom">
            <div>
              <span className="card-label">Card holder</span>
              <span className="card-value">{card.name_on_card}</span>
            </div>
            <div>
              <span className="card-label">Valid thru</span>
              <span className="card-value">{expiry(card)}</span>
            </div>
            <span className="card-sandbox">Sandbox</span>
          </div>
        </div>
        <div className="card-face card-back" aria-hidden={!flipped} inert={!flipped}>
          <div className="card-stripe" />
          <div className="card-sign">
            <span className="card-sign-lines" aria-hidden="true" />
            <span className="card-cvv" aria-label={`CVV ${cvv ?? ""}`}>{cvv ?? "•••"}</span>
          </div>
          <p className="card-fine">This card is issued by {bankName} for the game's sandbox. It moves only sandbox money.</p>
        </div>
      </div>
      {cvv && (
        <Button size="small" variant="ghost" aria-pressed={flipped} onClick={() => setFlipped((f) => !f)}>
          {flipped ? "Show front" : "Show CVV"}
        </Button>
      )}
    </div>
  );
}

/** Older playground keys were issued before card scopes existed. */
function scopeNotice(err: unknown) {
  if (err instanceof ApiError && err.code === "insufficient_scope") {
    return <div className="notice warn">This playground key was issued before cards existed. End the session (top right) and start a new one to try cards.</div>;
  }
  return null;
}

/** What "refunded" means for an authorisation that is only partly refunded. */
export function authAmountNote(a: Pick<CardAuthorization, "status" | "amount_minor" | "captured_minor" | "refunded_minor" | "currency">): string | null {
  if (a.status === "captured" && a.captured_minor < a.amount_minor) return `${formatMoney(a.captured_minor, a.currency)} captured`;
  if (a.refunded_minor > 0 && a.status !== "refunded") return `${formatMoney(a.refunded_minor, a.currency)} refunded`;
  return null;
}

export function CardsPanel({ api, gameId, account, bank, cardsOn }: { api: GameApi; gameId: string; account: Account; bank: Bank; cardsOn: boolean }) {
  const toast = useToast();
  const key = `sandbox:${gameId}:cards:${account.id}`;
  const cards = useQuery(cardsOn ? key : null, () => api.cards({ account_id: account.id }));
  const [issued, setIssued] = useState<IssueCardResult | null>(null);
  const [lost, setLost] = useState<Card | null>(null);
  const opKey = useMemo(() => newIdempotencyKey("card"), [issued]);
  const issue = useAction(() => api.issueCard({ account_id: account.id }, opKey));
  const act = useAction((c: Card, action: "block" | "unblock" | "report-lost" | "close", reason?: string) => api.cardAction(c.id, action, reason));
  if (!cardsOn) return null;
  const rows = cards.data?.data ?? [];

  const run = async (c: Card, action: "block" | "unblock" | "report-lost" | "close", done: string, reason?: string) => {
    if (await act.run(c, action, reason)) {
      invalidate(key);
      toast(done);
    }
  };

  return (
    <Panel title="Cards" sub="Debit cards on this account. Pay with one at a shop's merchant account." flush
      actions={<Button size="small" busy={issue.pending} disabled={account.status !== "active"} onClick={async () => {
        const r = await issue.run();
        if (r) {
          setIssued(r);
          invalidate(`sandbox:${gameId}`);
        }
      }}>Issue card</Button>}>
      {scopeNotice(cards.error ?? issue.error) ?? <ErrorNotice error={cards.error ?? issue.error ?? act.error} />}
      {cards.data && rows.length === 0 && <p className="muted small" style={{ padding: "0 20px 16px" }}>No cards yet.</p>}
      {rows.length > 0 && (
        <table className="data">
          <tbody>
            {rows.map((c) => {
              const st = CARD_STATUS[c.status];
              const items = [
                ...(c.status === "active" ? [{ label: "Block", onSelect: () => void run(c, "block", "Card blocked") }] : []),
                ...(c.status === "blocked" ? [{ label: "Unblock", onSelect: () => void run(c, "unblock", "Card unblocked") }] : []),
                ...(c.status === "active" || c.status === "blocked" ? [{ label: "Report lost or stolen", onSelect: () => setLost(c), danger: true }] : []),
                ...(c.status !== "closed" && c.status !== "lost" && c.status !== "stolen" ? [{ label: "Close", onSelect: () => void run(c, "close", "Card closed"), danger: true }] : []),
              ];
              return (
                <tr key={c.id}>
                  <td>
                    <div className="cell-title mono">{groupNumber(c.masked_number)}</div>
                    <div className="cell-sub">{c.name_on_card} · expires {String(c.expiry_month).padStart(2, "0")}/{c.expiry_year}</div>
                  </td>
                  <td>
                    <span className={`badge ${st.tone}`}>{st.label}</span>
                    {c.effective_daily_limit_minor != null && <div className="cell-sub">{formatMoney(c.effective_daily_limit_minor, account.currency)} a day</div>}
                  </td>
                  <td className="num">{items.length > 0 && <ActionMenu label={`Actions for card ${c.last4}`} items={items} />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <Modal open={!!issued} title="Card issued" onClose={() => setIssued(null)}
        description="A real game would show the number to the player now. It is never shown again."
        footer={<Button variant="primary" onClick={() => setIssued(null)}>Done</Button>}>
        {issued && (
          <div className="stack" style={{ gap: 16 }}>
            <CardFace number={issued.card_number} cvv={issued.cvv} card={issued.card} bankName={bank.name} color={bank.branding.primary_color} />
            <div className="notice warn" role="status">
              <strong>Copy the card details now.</strong> The full number and CVV won't be shown again.
            </div>
            <div className="row">
              <CopyButton text={issued.card_number} label="Copy card number" />
              <CopyButton text={issued.cvv} label="Copy CVV" />
              {issued.fees_total_minor > 0 && <span className="muted small" style={{ marginLeft: "auto" }}>Issuance fee {formatMoney(issued.fees_total_minor, account.currency)}</span>}
            </div>
          </div>
        )}
      </Modal>
      <Confirm open={!!lost} title="Report this card lost or stolen?" danger confirmLabel="Report"
        body={<>The card stops working for good and no replacement is issued. Payments already approved can still be captured by the shop.</>}
        busy={act.pending} error={act.error}
        onConfirm={() => { if (lost) void run(lost, "report-lost", "Card reported lost").then(() => setLost(null)); }}
        onClose={() => setLost(null)} />
    </Panel>
  );
}

export function PayByCard({ open, onClose, api, gameId, account, shops }: {
  open: boolean; onClose: () => void; api: GameApi; gameId: string; account: Account; shops: Account[];
}) {
  const toast = useToast();
  const cards = useQuery(open ? `sandbox:${gameId}:cards:${account.id}` : null, () => api.cards({ account_id: account.id }));
  const usable = (cards.data?.data ?? []).filter((c) => c.status === "active" || c.status === "blocked");
  const [cardId, setCardId] = useState("");
  const [shopId, setShopId] = useState("");
  const [amount, setAmount] = useState("");
  const [what, setWhat] = useState("");
  const [instant, setInstant] = useState(true);
  const opKey = useMemo(() => newIdempotencyKey("pay"), [open]);
  const card = usable.find((c) => c.id === cardId) ?? usable[0];
  const shop = shops.find((s) => s.id === shopId) ?? shops[0];
  const minor = parseMoney(amount, account.currency.exponent);
  const pay = useAction(() => api.authorizeCard({ card_id: card!.id, merchant_account_id: shop!.id, amount_minor: minor!, description: what.trim() || undefined,
    capture: instant }, opKey));
  return (
    <Modal open={open} onClose={onClose} title="Pay by card" description="Pay a shop with one of this account's cards."
      onSubmit={async (e) => {
        e.preventDefault();
        const r = await pay.run();
        if (!r) return;
        invalidate(`sandbox:${gameId}`);
        const a = r.authorization;
        toast(a.status === "captured" ? `Paid ${formatMoney(a.amount_minor, a.currency)} to ${a.merchant.name}`
          : a.status === "approved" ? `Approved: ${formatMoney(a.amount_minor, a.currency)} held for ${a.merchant.name}` : `Declined: ${a.decline_message ?? a.decline_code}`,
          a.status === "declined" ? "error" : undefined);
        onClose();
      }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={pay.pending} disabled={!card || !shop || !minor}>Pay</Button></>}>
      <div className="form">
        {scopeNotice(cards.error)}
        {cards.data && usable.length === 0 && <div className="notice">Issue a card on this account first.</div>}
        {shops.length === 0 && <div className="notice">Open a shop first: Open account, then choose Shop.</div>}
        {usable.length > 0 && (
          <Field label="Card">
            <Select value={card?.id} onChange={(e) => setCardId(e.target.value)}>
              {usable.map((c) => <option key={c.id} value={c.id}>{cardLabel(c)}{c.status === "blocked" ? " (blocked)" : ""}</option>)}
            </Select>
          </Field>
        )}
        {shops.length > 0 && (
          <Field label="Shop">
            <Select value={shop?.id} onChange={(e) => setShopId(e.target.value)}>
              {shops.map((s) => <option key={s.id} value={s.id}>{s.account_name} ({s.account_number})</option>)}
            </Select>
          </Field>
        )}
        <Field label={`Amount (${account.currency.code})`}>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="2,500" />
        </Field>
        <Field label="What for" hint="Optional; shown on the payment."><Input value={what} onChange={(e) => setWhat(e.target.value)} maxLength={140} placeholder="Generator" /></Field>
        <div className="field">
          <span id="pay-when">When the shop gets the money</span>
          <div className="choice-cards" role="radiogroup" aria-labelledby="pay-when">
            <label className="choice-card">
              <input type="radio" name="pay-when" checked={instant} onChange={() => setInstant(true)} />
              <strong>Charge now</strong>
              <small>The shop gets the money at once, like paying at a checkout counter.</small>
            </label>
            <label className="choice-card">
              <input type="radio" name="pay-when" checked={!instant} onChange={() => setInstant(false)} />
              <strong>Hold first</strong>
              <small>Set the amount aside. The shop takes it later, or cancels.</small>
            </label>
          </div>
        </div>
        <ErrorNotice error={pay.error} />
      </div>
    </Modal>
  );
}

/** Card payments made with this account's cards, or taken by this shop. */
export function CardPayments({ api, gameId, account, cardsOn }: { api: GameApi; gameId: string; account: Account; cardsOn: boolean }) {
  const toast = useToast();
  const shop = account.owner_type === "merchant" && account.merchant_id ? account.merchant_id : null;
  const q = useQuery(cardsOn ? `sandbox:${gameId}:card-auths:${account.id}` : null,
    () => api.cardAuthorizations(shop ? { merchant_id: shop, limit: 20 } : { account_id: account.id, limit: 20 }));
  const [capturing, setCapturing] = useState<CardAuthorization | null>(null);
  const [refunding, setRefunding] = useState<CardAuthorization | null>(null);
  const [amount, setAmount] = useState("");
  const opKey = useMemo(() => newIdempotencyKey("card-op"), [capturing, refunding]);
  const capture = useAction((a: CardAuthorization, minor?: number) => api.captureCard(a.id, { amount_minor: minor }, opKey));
  const refund = useAction((a: CardAuthorization, minor?: number) => api.refundCard(a.id, { amount_minor: minor }, opKey));
  const release = useAction((a: CardAuthorization) => api.releaseCard(a.id, newIdempotencyKey("release")));
  const rows = (q.data?.data ?? []).filter((a) => !shop || a.merchant_account_id === account.id);
  if (!cardsOn || (q.data && rows.length === 0)) return null;
  const minor = amount.trim() ? parseMoney(amount, account.currency.exponent) : undefined;
  const done = (msg: string) => { setCapturing(null); setRefunding(null); setAmount(""); invalidate(`sandbox:${gameId}`); toast(msg); };

  return (
    <Panel title={shop ? "Card payments taken" : "Card payments"} flush
      sub={shop ? "Capture an approved payment to receive it; release to cancel. Refund captured payments from this account." : "Payments made with this account's cards. The shop captures, releases or refunds them."}>
      {scopeNotice(q.error) ?? <ErrorNotice error={q.error ?? release.error} onRetry={q.reload} />}
      <table className="data">
        <tbody>
          {rows.map((a) => {
            const st = AUTH_STATUS[a.status];
            const note = authAmountNote(a);
            return (
              <tr key={a.id}>
                <td>
                  <div className="cell-title">{shop ? `Card •••• ${a.card_last4}` : a.merchant.name}{a.description ? ` · ${a.description}` : ""}</div>
                  <div className="cell-sub">
                    <span className={`badge ${st.tone}`}>{st.label}</span>{" "}
                    {a.status === "declined" ? a.decline_message : a.status === "approved" && a.expires_at_game ? `holds until ${formatGameTime(a.expires_at_game)}` : note}
                  </div>
                </td>
                <td className="num"><Money minor={a.amount_minor} currency={a.currency} /></td>
                <td className="num">
                  <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    {a.status === "approved" && <Button size="small" onClick={() => { setCapturing(a); setAmount(""); }}>Capture</Button>}
                    {a.status === "approved" && <Button size="small" busy={release.pending} onClick={async () => { if (await release.run(a)) done("Payment released"); }}>Release</Button>}
                    {a.status === "captured" && <Button size="small" onClick={() => { setRefunding(a); setAmount(""); }}>Refund</Button>}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Confirm open={!!capturing} title="Capture this card payment" confirmLabel="Capture" busy={capture.pending} error={capture.error}
        body={<>Moves the money into {capturing?.merchant.name}'s account and releases anything you don't capture.</>}
        onConfirm={async () => {
          if (!capturing || minor === null) return;
          const r = await capture.run(capturing, minor);
          if (r) done(r.fees_total_minor > 0 ? `Captured; card fee ${formatMoney(r.fees_total_minor, r.authorization.currency)}` : "Captured");
        }}
        onClose={() => setCapturing(null)}>
        <Field label={`Amount (${account.currency.code})`} hint={capturing ? `Leave empty to capture all ${formatMoney(capturing.amount_minor, capturing.currency)}.` : undefined}>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </Confirm>
      <Confirm open={!!refunding} title="Refund this card payment" confirmLabel="Refund" busy={refund.pending} error={refund.error}
        body={<>Sends money from {refunding?.merchant.name}'s account back to the card's account. Card fees are not refunded.</>}
        onConfirm={async () => {
          if (!refunding || minor === null) return;
          if (await refund.run(refunding, minor)) done("Refunded");
        }}
        onClose={() => setRefunding(null)}>
        <Field label={`Amount (${account.currency.code})`}
          hint={refunding ? `Leave empty to refund the rest: ${formatMoney(refunding.captured_minor - refunding.refunded_minor, refunding.currency)}.` : undefined}>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </Confirm>
    </Panel>
  );
}
